import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Copy, Download, Share2, X } from "lucide-react";
import { toast } from "sonner";

function wrapText(x, text, cx, startY, maxW, lh) {
  const words = text.split(/\s+/);
  let line = "";
  let y = startY;
  for (const w of words) {
    const t = line ? `${line} ${w}` : w;
    if (x.measureText(t).width > maxW && line) {
      x.fillText(line, cx, y);
      y += lh;
      line = w;
    } else {
      line = t;
    }
  }
  if (line) x.fillText(line, cx, y);
  return y;
}

export async function renderWishCard(w) {
  try {
    await Promise.all([
      document.fonts.load('italic 500 52px "Cormorant Garamond"'),
      document.fonts.load("700 40px Cinzel"),
      document.fonts.load('600 26px "JetBrains Mono"'),
    ]);
  } catch (e) {
    /* fall through with whatever is loaded */
  }
  const c = document.createElement("canvas");
  c.width = 1080;
  c.height = 1080;
  const x = c.getContext("2d");

  const g = x.createLinearGradient(0, 0, 0, 1080);
  g.addColorStop(0, "#00142D");
  g.addColorStop(1, "#002147");
  x.fillStyle = g;
  x.fillRect(0, 0, 1080, 1080);

  for (let i = 0; i < 130; i++) {
    x.globalAlpha = Math.random() * 0.45 + 0.08;
    x.fillStyle = "#D4AF37";
    x.beginPath();
    x.arc(Math.random() * 1080, Math.random() * 1080, Math.random() * 2.4 + 0.6, 0, Math.PI * 2);
    x.fill();
  }
  x.globalAlpha = 1;

  x.strokeStyle = "rgba(212,175,55,0.9)";
  x.lineWidth = 3;
  x.strokeRect(48, 48, 984, 984);
  x.strokeStyle = "rgba(212,175,55,0.35)";
  x.lineWidth = 1.5;
  x.strokeRect(68, 68, 944, 944);

  x.save();
  x.translate(540, 205);
  x.scale(1.25, 1.25);
  const shield = new Path2D("M0 -34 L28 -23 V4 C28 26 16 38 0 46 C-16 38 -28 26 -28 4 V-23 Z");
  x.lineWidth = 3;
  x.strokeStyle = "#D4AF37";
  x.fillStyle = "rgba(212,175,55,0.14)";
  x.fill(shield);
  x.stroke(shield);
  x.fillStyle = "#D4AF37";
  x.font = "700 32px Cinzel";
  x.textAlign = "center";
  x.fillText("91", 0, 12);
  x.restore();

  x.textAlign = "center";
  x.fillStyle = "#D4AF37";
  x.font = "600 25px 'JetBrains Mono'";
  x.fillText("THE DOON SCHOOL • 91ST FOUNDER'S DAY", 540, 366);

  x.fillStyle = "#F0ECE3";
  x.font = 'italic 500 52px "Cormorant Garamond"';
  const endY = wrapText(x, `"${w.message}"`, 540, 480, 820, 68);

  const sigY = Math.min(Math.max(endY + 80, 800), 830);
  x.fillStyle = "#D4AF37";
  x.font = "700 38px Cinzel";
  x.fillText(w.name.toUpperCase(), 540, sigY);
  x.fillStyle = "#9EAFC6";
  x.font = "500 23px 'JetBrains Mono'";
  x.fillText(`${w.house && w.house !== "—" ? `${w.house.toUpperCase()} · ` : ""}${w.role.toUpperCase()}`, 540, sigY + 44);

  const beam = x.createLinearGradient(240, 0, 840, 0);
  beam.addColorStop(0, "rgba(212,175,55,0)");
  beam.addColorStop(0.5, "rgba(212,175,55,0.9)");
  beam.addColorStop(1, "rgba(212,175,55,0)");
  x.fillStyle = beam;
  x.fillRect(240, sigY + 92, 600, 2);

  x.fillStyle = "rgba(158,175,198,0.85)";
  x.font = "500 21px 'JetBrains Mono'";
  x.fillText("KNOWLEDGE OUR LIGHT • EST. 1935 • CHANDBAGH, DEHRADUN", 540, sigY + 140);

  x.fillStyle = "rgba(158,175,198,0.55)";
  x.font = "500 17px 'JetBrains Mono'";
  x.fillText("MADE BY JAVEN WILSON P KHARSYNTIEW • 535 MH/T", 540, sigY + 172);

  return c.toDataURL("image/png");
}

export default function WishShareModal({ wish, onClose }) {
  const [dataUrl, setDataUrl] = useState(null);
  const [canShare, setCanShare] = useState(false);
  const generated = useRef(false);

  useEffect(() => {
    try {
      setCanShare(!!navigator.canShare?.({ files: [new File([new Blob(["x"])], "a.png", { type: "image/png" })] }));
    } catch (e) {
      setCanShare(false);
    }
  }, []);

  useEffect(() => {
    if (!wish || generated.current) return;
    generated.current = true;
    renderWishCard(wish).then(setDataUrl);
  }, [wish]);

  useEffect(() => {
    if (!wish) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [wish, onClose]);

  const download = () => {
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = "doon91-founders-day-wish.png";
    a.click();
    toast.success("Card saved — attach it in WhatsApp and spread the cheer");
  };

  const nativeShare = async () => {
    try {
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], "doon91-founders-day-wish.png", { type: "image/png" });
      await navigator.share({ files: [file], title: "91st Founder's Day", text: `${wish.message} — ${wish.name}` });
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Sharing was cancelled");
    }
  };

  const copyText = async () => {
    await navigator.clipboard.writeText(`"${wish.message}" — ${wish.name}, ${wish.role} • The Doon School 91st Founder's Day`);
    toast.success("Wish text copied");
  };

  return (
    <AnimatePresence>
      {wish && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[95] flex items-center justify-center p-4" data-testid="share-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="share-modal-backdrop" />
          <motion.div
            initial={{ y: 40, opacity: 0, scale: 0.96 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 20, opacity: 0 }}
            transition={{ type: "spring", damping: 26, stiffness: 240 }}
            className="relative z-10 max-h-[88vh] w-full max-w-md overflow-y-auto rounded-3xl border border-line/25 bg-ink p-6"
          >
            <button onClick={onClose} aria-label="Close" data-testid="share-modal-close" className="absolute right-5 top-5 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-ink/60 text-soft backdrop-blur transition-colors hover:text-gold">
              <X className="h-4 w-4" />
            </button>
            <h3 className="font-display text-lg font-bold text-fg" data-testid="share-modal-title">Shareable Wish Card</h3>
            <p className="mt-1 text-xs text-soft">Branded for the 91st Founder's Day — perfect for WhatsApp status.</p>
            <div className="mt-4 flex min-h-[280px] items-center justify-center rounded-2xl border border-line/15 bg-panel/40 p-3">
              {dataUrl ? (
                <img src={dataUrl} alt="Wish card preview" data-testid="share-card-preview" className="max-h-[48vh] rounded-xl" />
              ) : (
                <div className="flex flex-col items-center gap-3 py-10 text-soft">
                  <div className="h-10 w-10 animate-spin rounded-full border-2 border-gold border-t-transparent" />
                  <span className="text-xs tracking-widest">PAINTING YOUR CARD…</span>
                </div>
              )}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <button
                onClick={download}
                disabled={!dataUrl}
                data-testid="share-download-button"
                className="ripple-host flex items-center justify-center gap-2 rounded-full bg-gold py-3 text-xs font-bold tracking-widest text-navy transition-transform hover:scale-[1.02] disabled:opacity-50"
              >
                <Download className="h-4 w-4" /> DOWNLOAD
              </button>
              {canShare ? (
                <button
                  onClick={nativeShare}
                  disabled={!dataUrl}
                  data-testid="share-native-button"
                  className="glass flex items-center justify-center gap-2 rounded-full py-3 text-xs font-bold tracking-widest text-fg transition-all hover:border-gold/40 hover:text-gold disabled:opacity-50"
                >
                  <Share2 className="h-4 w-4" /> SHARE
                </button>
              ) : (
                <button
                  onClick={copyText}
                  data-testid="share-copy-button"
                  className="glass flex items-center justify-center gap-2 rounded-full py-3 text-xs font-bold tracking-widest text-fg transition-all hover:border-gold/40 hover:text-gold"
                >
                  <Copy className="h-4 w-4" /> COPY TEXT
                </button>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
